import React, { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { bumpReportUrls } from 'config/urls';
import { bumpReportChartParser, bumpReportComparisonChartParser } from 'parser/bumpReportChartParser';

const errorMessage: string = 'Something went wrong!';

interface Props {
    url: string;
    method: 'post' | 'get' | 'put' | 'delete';
    queryKey: any[];
    onError?: Function;
    variables?: any;
    appConfig?: any;
    queryConfig?: {
        enabled?: boolean;
    };
    extras: any;
    lineChartQueryConfig?: {
        enabled?: boolean;
    };
}

interface outputType {
    data: any;
    tableData: any;
    loading: boolean;
    error: any;
}

function useFetchBumpReportGraphRQ({
    url,
    method,
    onError,
    queryKey,
    variables = {},
    appConfig = {},
    queryConfig = {},
    lineChartQueryConfig = {},
    extras = {},
}: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [tableData, setTableData] = useState<any | null>(null);

    const bumpReportConfig = getConfig('bumpReport', appConfig);

    const y2Variables = {
        time_period: variables?.time_period,
        filters: variables?.filters,
        metrics: Array.isArray(extras?.metric) ? extras?.metric.map((item: any) => item.value) : [],
    };
    //react query to fetch incremental and promo tab data
    const {
        data: lineChartData,
        error: lineChartError,
        loading: lineChartLoading,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: lineChartQueryConfig,
        queryKey: ['bump-report-y2-line-chart', y2Variables],
        url: bumpReportUrls.lineChartUrl,
        variables: {
            ...y2Variables,
        },
        parserFunction: (data: any) => {
            if (data?.length === 0) throw 'No Data';
            return data;
        },
    });

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error: any) {
                onFetchError(error?.message);
                return { data: { error: error?.message } };
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }

    //error handling when fetching from cache
    if (apiData?.data && 'error' in apiData?.data && !error) {
        onFetchError(apiData?.data?.error);
    }

    useEffect(() => {
        //success handling
        if (
            apiSuccess &&
            apiData?.data &&
            queryConfig.enabled &&
            !lineChartLoading &&
            (lineChartQueryConfig.enabled ? lineChartData || lineChartError : true)
        ) {
            if (error) setError(null);
            onFetchComplete(lineChartError || !lineChartQueryConfig.enabled ? null : lineChartData, apiData.data);
        }
    }, [apiData?.data, apiSuccess, lineChartError, lineChartData, queryConfig.enabled, lineChartQueryConfig.enabled]);

    //called when fetching is complete
    function onFetchComplete(lineChartData: null | any[], payload: any) {
        try {
            let parsedData;
            //if no y2 data it means request coming from promo comparison
            if (variables.isComparison) {
                const { parsedData, tableData } = bumpReportComparisonChartParser(
                    {
                        y2Data: null,
                        barChartData: payload,
                    },
                    extras.from,
                    extras.y2AxisLabel,
                    bumpReportConfig,
                    appConfig,
                );
                setData(parsedData);
                setTableData(tableData);
                return;
            } else {
                parsedData = bumpReportChartParser(
                    {
                        y2Data: lineChartData,
                        barChartData: payload,
                    },
                    extras.from,
                    extras.y2AxisLabel,
                    bumpReportConfig,
                    appConfig,
                );
                setData(parsedData);
            }
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setTableData(null);
        setError(error);
    }
    const outputData: outputType = {
        loading: loading || lineChartLoading,
        error,
        data,
        tableData,
    };
    return outputData;
}

export { useFetchBumpReportGraphRQ };
